(ns pfo.auth
  (:require
    [reagent.core :as reagent :refer [atom]]
    [pfo.api :as api :refer [process-error]]
    [pfo.flash :refer [set-flash flash]]
    [reagent.session :as session]
    [promesa.core :as p]
    [pfo.valid :as valid]
    [pfo.common.component :refer [empty-page]]
    [secretary.core :as secretary :include-macros true]
    [pfo.state :as state]
    [pfo.util :as util]
    [pfo.auth.model :as auth-model]
    [pfo.auth.component :as component]
    [pfo.admin.component :as admin-component]
    [pfo.signature.component :as signature-component]
    [pfo.routes :as routes]))

(def auth-states #{:logged-in :logged-out :mfa-setup :mfa})

(defn update-auth-state [options response-obj]
  (let [success-state (or (api/get-auth-info :success-state) :logged-in)
        success-cb (api/get-auth-info :success-cb)]
    (when-let [auth-data (:data response-obj)]
     (do
       (api/set-auth-info :data auth-data)
       (cond
         ;; Very weird logic here, the default should be :logged-out
         (not-empty (:qrcode auth-data)) (api/set-auth-info :auth-state :mfa-setup)
         (not-empty (:code_url auth-data)) (api/set-auth-info :auth-state :mfa)
         :else (if success-cb
                 (success-cb)
                 (->> (api/ajaxp "/group_permissions/" {})
                   (p/map (fn [group-permissions-response]
                            (reset! state/subscriber-admin (set (map (fn [[k v]] (util/str->int (name k))) (group-permissions-response :subscriber_admin))))
                            (reset! state/can-review-documents (boolean (:can_review_documents group-permissions-response)))
                            (reset! state/is-novice (boolean (:is_novice group-permissions-response)))
                            (reset! state/group-permissions (into {} (map (fn [[k v]] [(util/str->int (name k)) (set (map keyword v))]) (group-permissions-response :family_permissions))))
                            (reset! state/permission-types
                              (clojure.set/union
                                (set (flatten (vals (:family_permissions group-permissions-response))))
                                (set (:global_permissions group-permissions-response))))
                            (reset! state/family-settings (update-vals (:family_settings group-permissions-response) (fn [m] (update-keys m (comp util/str->int name)))  ))

                            (reset! state/recent-parent-children {})

                            ;; Avoid dashboard permission errors after re-logging in as a license admin:
                            (when (and (= auth-model/user-type-license-admin (:user_type auth-data)) (not= :preauth success-state))
                              (util/set-page empty-page))
                            (api/set-auth-info :user-id (:user_id auth-data))
                            (api/set-auth-info :user-type (:user_type auth-data))
                            (api/set-auth-info :user {:first_name (:user_first_name auth-data)
                                                      :last_name (:user_last_name auth-data)
                                                      :early_access (:early_access auth-data)
                                                      :uses-mfa (:uses_mfa auth-data)
                                                      :is-bulk-editor (:is_bulk_editor auth-data)})
                            (api/set-auth-info :timezone (not-empty (:timezone auth-data)))
                            (api/set-auth-info :just-logged-in true)
                            (reset! state/flash {})
                            (reset! state/alert-count-recent {:value 0})
                            (if (and (= auth-model/user-type-license-admin (:user_type auth-data)) (not= :preauth success-state))
                              (do
                                (util/set-initial-page empty-page)
                                (api/set-auth-info :auth-state success-state)
                                (util/set-location "#/admin/user/list"))
                              (let [path (subs (.-hash (.-location js/window)) 1)]
                                (util/set-initial-page empty-page)
                                (api/set-auth-info :auth-state success-state)
                                (when-not (api/get-auth-info :skip-path-restore)
                                  (if (not-empty path)
                                    (if-let [[_ handler] (routes/get-handler path)]
                                      (handler)
                                      (secretary/dispatch! path))
                                    (util/set-location "#/")))))))
                   (process-error))))))))

(defn login [email password errors]
  (api/ajax @state/auth-url
            {:method :post
             :params  (into {} (util/remove-nil-values {:email @email
                                                :password @password
                                                :service_provider @state/auth-service-provider} ))}
            (fn [response]
              (api/set-auth-info :skip-path-restore false)
              (api/set-auth-info :success-state nil)
              (update-auth-state nil response))
            (fn [response]
              (.error js/console (str "auth failure" response))
              (reset! errors (if (= 401 (:status response))
                               (if (= "ip_restricted" (get-in response [:response :data :error_type]))
                                 {:password "Authentication blocked. Contact your Administrator."}
                                 {:password "Invalid email or password"})
                               (valid/field-errors (:response response)))))))

(defn login-demo [errors]
  (api/ajax "/authenticate/?demo=1"
            {:method :post
             :params {}}
            (fn [response]
              (update-auth-state nil response))

            (fn [response]
              (.error js/console (str "auth failure" response)))))

(defn login-with-token [options token]
  (->> (api/ajaxp "/authenticate/"
              {:method :post
               :params {:token token}})
       (p/map (fn [response]
                (api/set-auth-info :skip-path-restore (:skip-path-restore options))
                (api/set-auth-info :success-state (:success-state options))
                (update-auth-state options response)))))

(defn mfa-code-submit [code-url code errors]
  (api/ajax code-url
            {:method :post
             :params (into {} (util/remove-nil-values 
                        {:code @code
                         :service_provider @state/auth-service-provider}))}
            (fn [response]
              (.info js/console "mfa code submit success")
              (update-auth-state nil response))
            (fn [response]
              (.error js/console (str "auth failure" response))
              (reset! errors (if
                               (= 401 (:status response))
                               {:code "Invalid code"}
                               (valid/field-errors (:response response)))))))

(defn render-login [form-component]
  (fn [form-component]
    [:div.o-fullscreen.c-login.o-center
     [flash]
     [:div.o-media.c-login__container.o-center__child.u-shadow--around
      [:div.c-login__logo]
      form-component]])) 

(defn login-form []
  (let [email (atom "")
        password (atom "")
        errors (atom {})
        login-submit (fn [e]
                       (login email password errors)
                       (.preventDefault e))]

    (fn []
      [:form {:on-submit login-submit}
       [:dl 
        [:dd 
         [:input.c-login__input {:type "text" :required "required" :value @email
                                 :on-change #(reset! email (-> % .-target .-value))
                                 :placeholder "Email"
                                 :autoCapitalize "off"
                                 :autoCorrect "off"}]
         [:span {:class "error"} (:email @errors "")]]]

       [:dl 
        [:dd
         [:input.c-login__input {:type "password" :required "required" :value @password
                                 :on-change #(reset! password (-> % .-target .-value))
                                 :placeholder "Password"
                                 :autoCapitalize "off"
                                 :autoCorrect "off"}]]

        [:dd.c-login__password-reset
         [:small [:a {:href "#/password-reset-init/" } "Forgot your password?"]]]]

       [:dl
        [:dd.error (:password @errors "")]]

       [:footer.c-login__footer 
        [:button.c-button--dark-blue {:type :submit} "Sign In"]]
       [:div.c-login__patent "Protected by one or more of the following US Patents: #10,789,572; #11,631,051; #12,314,903."]])))



(def go-to-login (util/handler (fn []
                                 (reset! state/group-permissions #{})
                                 (api/set-auth-info :auth-state :logged-out)
                                 (util/set-location "#/"))))

(defn password-reset-email-form []
  (let [sent (atom false)
        email (atom nil)]
    (fn []
      [:div

       (if @sent
         [:div
          [:p "Password reset instructions have been sent."]
          [:footer.c-login__footer
           [:button.c-button {:on-click go-to-login} "Go to login page"]]]

         [:div
          [:p "Forgotten your password? Enter your e-mail address below, and we'll e-mail instructions for setting a new one."]
          [:form {:on-submit (util/handler (fn []
                                             (->> (auth-model/password-reset-request @email))
                                             (reset! sent true)))}

           [:dl
            [:dd 
             [:input.c-login__input {:type :email
                      :required :required
                      :on-change (util/reset-from-event email)}]]] 

           [:footer.o-margin-elements--xs.c-login__footer
            [:button.c-button--dark-blue {:on-click go-to-login :type :button} "Cancel"]
            [:button.c-button--dark-blue {:type :submit} "Send"]]]])])))

(defn login-screen []
  [render-login [login-form]])

(defn- mfa-cancel [] (api/set-auth-info :auth-state :logged-out))

(defn mfa-setup-screen [code-url qrcode]
  [render-login [component/mfa {:cancel-cb mfa-cancel
                                :success-cb mfa-code-submit} code-url qrcode]])

(defn mfa-screen [code-url]
  [render-login [component/mfa {:cancel-cb mfa-cancel
                                :success-cb mfa-code-submit} code-url]])

(defn password-reset-init []
  [render-login [password-reset-email-form]])

(defn password-reset [token]
  [render-login [admin-component/user-password-reset-form token]])

(def LoginForm (reagent/reactify-component login-form))

(defn force-login [component authenticated-page]
  (if (:login-layout (meta @component))
    [render-login @component]
    (let [data (api/get-auth-info :data)]
      (case (api/get-auth-info :auth-state)
        :logged-in [authenticated-page @component]
        :preauth [render-login @component]
        :mfa [mfa-screen (:code_url data)]
        :mfa-setup [mfa-setup-screen (:code_url data) (:qrcode data)]
        :password-reset-init [password-reset-init]
        :password-reset [password-reset (api/get-auth-info :password-reset-token)]
        [login-screen]))))

(defn admin-auth [event]
  (api/ajax "/authenticate/admin/" {}
            (fn [response]
              (set! (.-location js/window) "/admin/users/")))
  (.preventDefault event))
